/**
 * The prototype's small "i" beside a label (design export 2026-10-09: New campaign's name, local time
 * and wait; Contact lists' column choices): a 20 px button that opens a tooltip on hover, on focus and
 * on click, and closes on leave, on blur and on Escape. The tooltip is the button's description
 * (`aria-describedby`), so a screen reader hears it with the button.
 */
import { Icon } from "@wetalk/ui";
import { useId, useState, type ReactNode } from "react";

import { CAMPAIGN_GLYPH } from "./campaign-glyph.js";

import "./info-tip.css";

export interface InfoTipProps {
  /** The button's name: "How local time is worked out". */
  readonly label: string;
  readonly children: ReactNode;
  /** Which edge the tooltip hangs from: the design opens most to the right, Contact lists' to the left. */
  readonly align?: "start" | "end";
  /** The design's 260 px name tooltip is narrower than the others. */
  readonly narrow?: boolean;
}

export function InfoTip({ label, children, align = "start", narrow = false }: InfoTipProps): ReactNode {
  const tipId = useId();
  const [open, setOpen] = useState(false);
  return (
    <span
      className="c-info-tip"
      onMouseEnter={() => {
        setOpen(true);
      }}
      onMouseLeave={() => {
        setOpen(false);
      }}
    >
      <button
        type="button"
        className="c-info-tip-button"
        aria-label={label}
        aria-describedby={open ? tipId : undefined}
        aria-expanded={open}
        onClick={() => {
          setOpen(true);
        }}
        onFocus={() => {
          setOpen(true);
        }}
        onBlur={() => {
          setOpen(false);
        }}
        onKeyDown={(event) => {
          if (event.key === "Escape") setOpen(false);
        }}
      >
        <Icon glyph={CAMPAIGN_GLYPH.info} size="md" />
      </button>
      {open ? (
        <span id={tipId} role="tooltip" className="c-info-tip-body" data-align={align} data-narrow={narrow}>
          {children}
        </span>
      ) : null}
    </span>
  );
}
